import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../shadcn-ui/card';
import { CircleAlert, Pencil, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../shadcn-ui/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import createLotSchema from '@/schema/create-lot-schema';
import { Controller, useForm } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../shadcn-ui/select';
import { useEstateStore } from '@/stores/useEstate.store';
import { Label } from '../shadcn-ui/label';
import { Input } from '../shadcn-ui/input';
import { cn } from '@/lib/utils';
import { DatePicker } from '../date-picker';
import { Button } from '../shadcn-ui/button';
import { updateLot } from '@/network/api.service';
import { toast } from 'sonner';
import { useMasterStore } from '@/stores/useMaster.store';
import { useLotStore } from '@/stores/useLot.store';

const LotCard = ({ lot, edit = false }) => {
  const [open, setOpen] = useState(false);
  const estates = useEstateStore(state => state.estates);
  const [loading, setLoading] = useState(false);
  const varietalList = useMasterStore(state => state.varietalList);

  const {
    control,
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createLotSchema),
    defaultValues: {
      estate_id: lot?.estate?._id,
      varietal: lot?.varietal,
      weight_of_coffee: lot?.weight_of_coffee,
      date_of_harvest: lot?.date_of_harvest ? new Date(lot.date_of_harvest) : null,
      date_of_sourcing: lot?.date_of_sourcing ? new Date(lot.date_of_sourcing) : null,
    },
  });

  useEffect(() => {
    if (lot) {
      reset({
        estate_id: lot?.estate?._id || lot?.estate_id?._id || lot?.estate_id || '',
        varietal: lot?.varietal || '',
        weight_of_coffee: lot?.weight_of_coffee || '',
        date_of_harvest: lot?.date_of_harvest ? new Date(lot.date_of_harvest) : null,
        date_of_sourcing: lot?.date_of_sourcing ? new Date(lot.date_of_sourcing) : null,
      });
    }
  }, [lot, reset, open]);

  const onSubmit = async data => {
    try {
      setLoading(true);

      const { data: respData } = await updateLot(data, lot?._id);

      if (respData.success) {
        toast.success('Lot Updated');

        useLotStore.getState().setLot({ ...respData.data });
        setOpen(false);
      } else {
        toast.error('Faile to update lot');
      }
    } catch (error) {
      console.log(error);

      toast.error('failed to update lot data.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-2xl px-4 py-5 shadow-sm">
      <div className="mb-2 flex items-start justify-between">
        <div className="text-base font-semibold">Lot Information</div>
        {edit ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button type="button" className="rounded-full p-1 hover:bg-muted" aria-label="Edit lot information">
                <Pencil className="h-4 w-4" />
              </button>
            </DialogTrigger>

            <DialogContent
              className={cn(
                'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                'w-[calc(100%-32px)] max-w-md rounded-2xl p-0 [&>button]:hidden'
              )}
            >
              <DialogTitle className="hidden">Lot Update</DialogTitle>

              <Card className="rounded-2xl border border-border shadow-sm">
                <CardContent className="space-y-3 pt-3">
                  {/* Estate Name */}
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                        Estate<span className="">*</span>
                      </Label>

                      <Controller
                        name="estate_id"
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="rounded-[10px] bg-white">
                              <SelectValue placeholder="Select estate" />
                            </SelectTrigger>
                            <SelectContent>
                              {estates?.map(est => (
                                <SelectItem key={est._id} value={est._id}>
                                  {est.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />

                      {errors?.estate_id && <div className="text-xs text-red-500">{errors.estate_id.message}</div>}
                    </div>

                    {/* Varietal Name */}
                    <div className="space-y-1.5">
                      <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                        Varietal<span className="">*</span>
                      </Label>

                      <Controller
                        name="varietal"
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="rounded-[10px] bg-white">
                              <SelectValue placeholder="Select varietal" />
                            </SelectTrigger>
                            <SelectContent>
                              {varietalList?.map(({ varietal, _id }) => (
                                <SelectItem key={_id} value={varietal}>
                                  {varietal}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors?.varietal && <div className="text-xs text-red-500">{errors.varietal.message}</div>}
                    </div>

                    {/* Wieght of coffee */}
                    <div className="space-y-1.5">
                      <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                        Weight of coffee (kg)<span className="">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          disabled={loading}
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          className={cn(
                            'rounded-[10px] bg-white text-sm font-normal',
                            errors?.weight_of_coffee && 'border-red-500'
                          )}
                          {...register('weight_of_coffee', {
                            valueAsNumber: true,
                          })}
                        />
                        {errors?.weight_of_coffee && (
                          <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />
                        )}
                      </div>
                      {errors?.weight_of_coffee && (
                        <div className="text-xs text-red-500">{errors.weight_of_coffee.message}</div>
                      )}
                    </div>

                    {/* Date of harvest */}
                    <div className="space-y-1.5">
                      <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                        Date of harvest<span className="">*</span>
                      </Label>

                      <Controller
                        name="date_of_harvest"
                        control={control}
                        render={({ field }) => <DatePicker value={field?.value} onChange={field?.onChange} />}
                      />
                    </div>

                    {/* Date of sourcing */}
                    <div className="space-y-1.5">
                      <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                        Date of sourcing<span className="">*</span>
                      </Label>

                      <Controller
                        name="date_of_sourcing"
                        control={control}
                        render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Dialog for double confirmation */}
                      <ConfirmationDialog loading={loading} onSubmit={handleSubmit(onSubmit)} />
                      {/* <Button onClick={() => setConfirmOpen(true)} className="w-full">
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save details
                      </Button> */}

                      <DialogClose asChild>
                        <Button className="w-full" disabled={loading}>
                          Cancel
                        </Button>
                      </DialogClose>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </DialogContent>
          </Dialog>
        ) : null}
      </div>

      <div className="space-y-1 text-sm">
        <div>
          <span className="font-semibold">Lot:</span> {lot?.lotname}
        </div>
        <div>
          <span className="font-semibold">Estate:</span> {lot?.estate?.name || lot?.estate_id?.name}
        </div>
        <div>
          <span className="font-semibold">Varietal:</span> {lot?.varietal}
        </div>
        <div>
          <span className="font-semibold">Weight of coffee:</span> {lot?.weight_of_coffee}
        </div>
        <div>
          <span className="font-semibold">Date of harvest:</span>
          {new Date(lot?.date_of_harvest).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
        <div>
          <span className="font-semibold">Date of sourcing:</span>

          {new Date(lot?.date_of_sourcing).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
      </div>
    </Card>
  );
};

export default LotCard;

const ConfirmationDialog = ({ loading, onSubmit }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="w-full">Save details</Button>
      </DialogTrigger>
      <DialogContent
        className={cn(
          'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 gap-6',
          'w-[calc(100%-32px)] max-w-md rounded-2xl p-6 [&>button]:hidden'
        )}
      >
        <DialogHeader className="hidden">
          <DialogTitle className="hidden">Edit profile</DialogTitle>
          <DialogDescription className="hidden" />
        </DialogHeader>
        <div className="relative flex flex-col items-center justify-center gap-6">
          <DialogClose asChild>
            <X className="absolute right-0 top-0" />
          </DialogClose>
          <Pencil className="size-10 text-[#9095A1FF]" />
          <div className="">Are you sure you want to edit?</div>
          <div className="flex items-center gap-2">
            <Button className="w-[75px] max-w-full" onClick={onSubmit}>
              Yes
            </Button>
            <DialogClose asChild>
              <Button variant="outline" className="w-full" disabled={loading}>
                Close
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
